import React, { useContext, useEffect, useState } from "react";
import { Button, TextField, Typography, List, ListItem, ListItemText, CircularProgress, Tabs, Tab, Box } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useSystemMailbox from "../../pages/Settings/hooks/useSystemMailbox";
import { getEmailTemplate, listEmailTemplates, testEmailTemplate, updateEmailTemplate } from "../../services/emailChannel.api";

/**
 * Pestaña «Plantillas» del modal de una conexión de correo (doc 0445 E4b). Las plantillas solo existen
 * porque una conexión de correo las envía: viven con ella, no en una pantalla aparte. Son de la
 * PLATAFORMA (alertas del VPS), así que solo las ve el superadmin.
 */
const EmailTemplatesPanel = ({ whatsappId, onEditingChange }) => {
  const { user } = useContext(AuthContext);
  const { mailbox, candidates, loading: cargandoBuzon, saving: eligiendo, choose } = useSystemMailbox();
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [actual, setActual] = useState(null);
  const [original, setOriginal] = useState(null);
  const [vista, setVista] = useState("html");
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);

  useEffect(() => {
    listEmailTemplates()
      .then(({ data }) => setLista(Array.isArray(data) ? data : []))
      .catch(toastError)
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    onEditingChange && onEditingChange(Boolean(actual));
  }, [actual, onEditingChange]);

  const abrir = async (id) => {
    try {
      const { data } = await getEmailTemplate(id);
      setActual(data);
      setOriginal(data);
      setVista("html");
    } catch (err) {
      toastError(err);
    }
  };

  const cambiar = (campo) => (e) => setActual((p) => ({ ...p, [campo]: e.target.value }));

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await updateEmailTemplate(actual.id, { subject: actual.subject, htmlContent: actual.htmlContent, textContent: actual.textContent });
      setActual(data);
      setOriginal(data);
      setLista((l) => l.map((t) => (t.id === data.id ? { ...t, subject: data.subject } : t)));
      toast.success("Plantilla guardada");
    } catch (err) {
      toastError(err);
    } finally {
      setGuardando(false);
    }
  };

  const probar = async () => {
    setProbando(true);
    try {
      const { data } = await testEmailTemplate(actual.id, user?.email);
      toast.success(`Prueba enviada a ${data.to}`);
    } catch (err) {
      toastError(err);
    } finally {
      setProbando(false);
    }
  };

  // La prueba la renderiza el servidor con lo GUARDADO: con cambios pendientes llegaría la versión vieja.
  const sinGuardar = Boolean(actual && original && ["subject", "htmlContent", "textContent"].some((k) => (actual[k] || "") !== (original[k] || "")));
  const esEsta = mailbox && Number(mailbox.whatsappId) === Number(whatsappId);
  const puedeSerlo = candidates.some((c) => Number(c.whatsappId) === Number(whatsappId));

  const estado = cargandoBuzon ? null : esEsta ? (
    <Alert severity="success" style={{ marginBottom: 12 }}>
      Esta conexión envía los correos del sistema: alertas del servidor y recuperación de contraseña.
    </Alert>
  ) : (
    <Alert
      severity={mailbox ? "info" : "warning"}
      style={{ marginBottom: 12 }}
      action={
        puedeSerlo ? (
          <Button color="inherit" size="small" disabled={eligiendo} onClick={() => choose(whatsappId)}>
            {eligiendo ? <CircularProgress size={16} /> : "Usar esta"}
          </Button>
        ) : null
      }
    >
      {mailbox
        ? <>Los correos del sistema salen ahora por <b>{mailbox.address}</b>.</>
        : "Ninguna conexión envía todavía los correos del sistema: sin ella, estas plantillas no se usan."}
    </Alert>
  );

  if (!actual) {
    return (
      <>
        {estado}
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
          Lo que reciben los administradores cuando el servidor avisa. Pulsa una para editarla.
        </Typography>
        {cargando ? (
          <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
        ) : (
          <List dense disablePadding>
            {lista.map((t) => (
              <ListItem key={t.id} button divider onClick={() => abrir(t.id)}>
                <ListItemText primary={t.name} secondary={t.subject} />
              </ListItem>
            ))}
            {!lista.length && <Typography variant="body2" color="textSecondary">No hay plantillas.</Typography>}
          </List>
        )}
      </>
    );
  }

  return (
    <>
      {estado}
      <Typography variant="subtitle2" style={{ fontWeight: 600 }}>{actual.name}</Typography>
      {actual.description && <Typography variant="caption" color="textSecondary" component="div">{actual.description}</Typography>}
      <TextField label="Asunto" value={actual.subject || ""} onChange={cambiar("subject")} variant="outlined" size="small" fullWidth margin="dense" />
      {actual.variables?.length > 0 && (
        <Typography variant="caption" color="textSecondary" component="div" style={{ margin: "4px 0 8px" }}>
          Variables: {actual.variables.map((v) => `{${v}}`).join(" · ")}
        </Typography>
      )}
      <Tabs value={vista} onChange={(_, v) => setVista(v)} indicatorColor="primary" textColor="primary" style={{ minHeight: 36 }}>
        <Tab value="html" label="HTML" style={{ minHeight: 36 }} />
        <Tab value="vista" label="Vista previa" style={{ minHeight: 36 }} />
        <Tab value="texto" label="Texto" style={{ minHeight: 36 }} />
      </Tabs>
      {vista === "html" && (
        <TextField value={actual.htmlContent || ""} onChange={cambiar("htmlContent")} variant="outlined" fullWidth multiline minRows={12} maxRows={20}
          inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }} style={{ marginTop: 8 }} />
      )}
      {vista === "vista" && (
        <iframe title="vista previa" sandbox="" srcDoc={actual.htmlContent || ""} style={{ width: "100%", height: 380, border: "1px solid rgba(127,127,127,.3)", borderRadius: 6, marginTop: 8, background: "#fff" }} />
      )}
      {vista === "texto" && (
        <TextField value={actual.textContent || ""} onChange={cambiar("textContent")} variant="outlined" fullWidth multiline minRows={10}
          helperText="Para los lectores de correo que no muestran HTML" style={{ marginTop: 8 }} />
      )}
      <Box display="flex" alignItems="center" mt={1.5} style={{ gap: 8 }}>
        <Button onClick={() => setActual(null)} disabled={guardando}>← Plantillas</Button>
        <Box flex={1} />
        <Button onClick={probar} disabled={probando || guardando || sinGuardar || !mailbox || !user?.email} title={sinGuardar ? "Guarda antes de enviar la prueba" : ""}>
          {probando ? <CircularProgress size={18} /> : "Enviar prueba"}
        </Button>
        <Button color="primary" variant="contained" onClick={guardar} disabled={guardando}>
          {guardando ? <CircularProgress size={18} /> : "Guardar plantilla"}
        </Button>
      </Box>
    </>
  );
};

export default EmailTemplatesPanel;
